import { createPortal } from 'react-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../components/Icon'
import { getLocale } from '../lib/i18n'

/**
 * Get a photo ready before it is listed: turn it, flip it, crop it, and lift the light. Nothing leaves the phone until the
 * person taps "Use photo"; the edited picture then goes through the same preparePhoto as any other. "Keep as it is" skips it all.
 * The crop box and the sliders work in fractions of the turned picture, so the preview and the saved file always agree.
 * Colours come from the look through the same variables the tour and feedback sheet use.
 */
const L = (en: string, es: string) => (getLocale() === 'en' ? en : es)
type Box = { x: number; y: number; w: number; h: number }
type Shape = 'free' | '1:1' | '4:3' | '3:4' | '16:9'
const SHAPES: Shape[] = ['free', '1:1', '4:3', '3:4', '16:9']
const RATIO: Record<Shape, number | null> = { free: null, '1:1': 1, '4:3': 4 / 3, '3:4': 3 / 4, '16:9': 16 / 9 }
const shapeLabel = (s: Shape) => (s === 'free' ? L('Free', 'Libre') : s)
const MIN = 0.1
const MAX_SIDE = 2400
const full: Box = { x: 0, y: 0, w: 1, h: 1 }
/** Canvas filters are missing in some older Safari; without them the light sliders are hidden rather than doing nothing. */
const canFilter = () => typeof CanvasRenderingContext2D !== 'undefined' && 'filter' in CanvasRenderingContext2D.prototype

/** The largest box of this shape (width / height, in pixels) that fits the turned picture, centred. */
export function boxFor(shape: Shape, pw: number, ph: number): Box {
  const r = RATIO[shape]
  if (!r) return full
  const w = Math.min(1, (r * ph) / pw), h = Math.min(1, pw / r / ph)
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h }
}
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

type Edit = { turns: number; flip: boolean; box: Box; light: { b: number; c: number; s: number } }

/** Draws the whole picture turned and flipped, with the light applied, at most `max` pixels on the long side. */
function drawTurned(bitmap: ImageBitmap, edit: Edit, max: number): HTMLCanvasElement {
  const odd = edit.turns % 2 === 1
  const sw = odd ? bitmap.height : bitmap.width, sh = odd ? bitmap.width : bitmap.height
  const k = Math.min(1, max / Math.max(sw, sh))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(sw * k)); canvas.height = Math.max(1, Math.round(sh * k))
  const ctx = canvas.getContext('2d')!
  if (canFilter()) ctx.filter = `brightness(${edit.light.b}%) contrast(${edit.light.c}%) saturate(${edit.light.s}%)`
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((edit.turns * Math.PI) / 2)
  if (edit.flip) ctx.scale(-1, 1)
  const dw = bitmap.width * k, dh = bitmap.height * k
  ctx.drawImage(bitmap, -dw / 2, -dh / 2, dw, dh)
  return canvas
}

/** The edited picture as a JPEG file, cropped to the box. */
export async function renderEdit(bitmap: ImageBitmap, edit: Edit, name: string): Promise<File> {
  const whole = drawTurned(bitmap, edit, MAX_SIDE)
  const cw = Math.max(1, Math.round(whole.width * edit.box.w)), ch = Math.max(1, Math.round(whole.height * edit.box.h))
  const out = document.createElement('canvas')
  out.width = cw; out.height = ch
  out.getContext('2d')!.drawImage(whole, Math.round(whole.width * edit.box.x), Math.round(whole.height * edit.box.y), cw, ch, 0, 0, cw, ch)
  const blob = await new Promise<Blob>((resolve, reject) => out.toBlob(b => (b ? resolve(b) : reject(new Error('photo_type'))), 'image/jpeg', 0.9))
  return new File([blob], name.replace(/\.[^.]+$/, '') + '-edited.jpg', { type: 'image/jpeg' })
}

type Drag = { mode: 'move' | 'nw' | 'ne' | 'sw' | 'se'; startX: number; startY: number; box: Box } | null

export default function PhotoEditor({ file, onDone, onCancel }: { file: File; onDone: (edited: File) => void; onCancel: () => void }) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null)
  const [failed, setFailed] = useState(false)
  const [turns, setTurns] = useState(0)
  const [flip, setFlip] = useState(false)
  const [shape, setShape] = useState<Shape>('free')
  const [box, setBox] = useState<Box>(full)
  const [light, setLight] = useState({ b: 100, c: 100, s: 100 })
  const [busy, setBusy] = useState(false)
  const view = useRef<HTMLCanvasElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const drag = useRef<Drag>(null)
  const card = useRef<HTMLDivElement>(null)
  const filters = useMemo(canFilter, [])

  useEffect(() => {
    let alive = true, made: ImageBitmap | null = null
    createImageBitmap(file).then(b => { made = b; if (alive) setBitmap(b); else b.close() }).catch(() => { if (alive) setFailed(true) })
    return () => { alive = false; made?.close() }
  }, [file])
  // A file the browser cannot decode here still goes through as it is; preparePhoto gives the usual error if it truly is not a picture.
  useEffect(() => { if (failed) onDone(file) }, [failed, file, onDone])
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel() }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [onCancel])
  useEffect(() => { card.current?.querySelector<HTMLElement>('button')?.focus() }, [])

  const odd = turns % 2 === 1
  const pw = bitmap ? (odd ? bitmap.height : bitmap.width) : 1, ph = bitmap ? (odd ? bitmap.width : bitmap.height) : 1
  // The preview is redrawn small; the saved file is drawn big.
  useEffect(() => {
    if (!bitmap || !view.current) return
    const src = drawTurned(bitmap, { turns, flip, box: full, light }, 960)
    const c = view.current
    c.width = src.width; c.height = src.height
    c.getContext('2d')!.drawImage(src, 0, 0)
  }, [bitmap, turns, flip, light])

  const reshape = useCallback((nextShape: Shape, nextTurns: number) => {
    const o = nextTurns % 2 === 1
    setBox(bitmap ? boxFor(nextShape, o ? bitmap.height : bitmap.width, o ? bitmap.width : bitmap.height) : full)
  }, [bitmap])
  const turn = (d: number) => { const t = (turns + d + 4) % 4; setTurns(t); reshape(shape, t) }
  const pickShape = (s: Shape) => { setShape(s); reshape(s, turns) }
  const reset = () => { setTurns(0); setFlip(false); setShape('free'); setBox(full); setLight({ b: 100, c: 100, s: 100 }) }
  const changed = turns !== 0 || flip || box !== full || light.b !== 100 || light.c !== 100 || light.s !== 100

  const down = (mode: NonNullable<Drag>['mode']) => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    drag.current = { mode, startX: e.clientX, startY: e.clientY, box }
  }
  const move = (e: React.PointerEvent) => {
    const d = drag.current, rect = stage.current?.getBoundingClientRect()
    if (!d || !rect) return
    const dx = (e.clientX - d.startX) / rect.width, dy = (e.clientY - d.startY) / rect.height
    const b = d.box
    if (d.mode === 'move') { setBox({ ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) }); return }
    // Each corner moves two edges; the opposite corner stays where it is.
    const left = d.mode === 'nw' || d.mode === 'sw', top = d.mode === 'nw' || d.mode === 'ne'
    const anchorX = left ? b.x + b.w : b.x, anchorY = top ? b.y + b.h : b.y
    let cx = clamp((left ? b.x : b.x + b.w) + dx, 0, 1), cy = clamp((top ? b.y : b.y + b.h) + dy, 0, 1)
    let w = Math.max(MIN, Math.abs(cx - anchorX)), h = Math.max(MIN, Math.abs(cy - anchorY))
    const r = RATIO[shape]
    if (r) {
      // Keep the shape: follow whichever side was dragged further, then fit inside the picture.
      const wFromH = (h * ph * r) / pw
      if (w / wFromH > 1) h = (w * pw) / r / ph; else w = wFromH
      const room = { w: left ? anchorX : 1 - anchorX, h: top ? anchorY : 1 - anchorY }
      const k = Math.min(1, room.w / w, room.h / h)
      w *= k; h *= k
    }
    cx = left ? anchorX - w : anchorX + w; cy = top ? anchorY - h : anchorY + h
    setBox({ x: Math.min(anchorX, cx), y: Math.min(anchorY, cy), w, h })
  }
  const up = () => { drag.current = null }

  const save = async () => {
    if (!bitmap || busy) return
    setBusy(true)
    try { onDone(await renderEdit(bitmap, { turns, flip, box, light }, file.name)) } catch { onDone(file) } finally { setBusy(false) }
  }
  const slider = (key: 'b' | 'c' | 's', label: string) => <label className="pe-slider">{label}
    <input type="range" min="50" max="150" value={light[key]} onChange={e => setLight(l => ({ ...l, [key]: Number(e.target.value) }))}/>
  </label>

  return createPortal(<div className="fb-layer pe-layer" role="presentation">
    <div ref={card} className="fb-sheet pe-sheet" role="dialog" aria-modal="true" aria-labelledby="pe-title">
      <div className="pe-head">
        <h2 id="pe-title">{L('Get your photo ready', 'Prepara tu foto')}</h2>
        <button type="button" className="fb-close" aria-label={L('Cancel', 'Cancelar')} onClick={onCancel}><Icon name="close" size={18}/></button>
      </div>
      {!bitmap ? <p role="status" className="pe-body">{L('Opening your photo…', 'Abriendo tu foto…')}</p> : <>
        <div className="pe-body">
        <div className="pe-pad" onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <div className="pe-stage" ref={stage} style={{ aspectRatio: `${pw} / ${ph}`, width: `min(100%, ${(46 * pw) / ph}dvh)` }}>
          <canvas ref={view} className="pe-view" role="img" aria-label={L('Your photo, with the crop box on it', 'Tu foto, con el cuadro de recorte')}/>
          <div className="pe-dim" aria-hidden="true" style={{ clipPath: `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${box.x * 100}% ${box.y * 100}%, ${box.x * 100}% ${(box.y + box.h) * 100}%, ${(box.x + box.w) * 100}% ${(box.y + box.h) * 100}%, ${(box.x + box.w) * 100}% ${box.y * 100}%, ${box.x * 100}% ${box.y * 100}%)` }}/>
          <div className="pe-box" data-testid="crop-box" style={{ left: `${box.x * 100}%`, top: `${box.y * 100}%`, width: `${box.w * 100}%`, height: `${box.h * 100}%` }} onPointerDown={down('move')}>
            {(['nw', 'ne', 'sw', 'se'] as const).map(c => <span key={c} className={`pe-handle pe-${c}`} onPointerDown={down(c)} aria-hidden="true"/>)}
          </div>
        </div>
        </div>
        <div className="pe-tools" role="group" aria-label={L('Turn and flip', 'Girar y voltear')}>
          <button type="button" onClick={() => turn(-1)}><Icon name="rotate-left" size={18}/>{L('Turn left', 'Girar izquierda')}</button>
          <button type="button" onClick={() => turn(1)}><Icon name="rotate-right" size={18}/>{L('Turn right', 'Girar derecha')}</button>
          <button type="button" aria-pressed={flip} className={flip ? 'on' : ''} onClick={() => setFlip(f => !f)}><Icon name="flip" size={18}/>{L('Flip', 'Voltear')}</button>
        </div>
        <div role="radiogroup" aria-label={L('Crop shape', 'Forma del recorte')} className="fb-kinds pe-shapes">
          {SHAPES.map(s => <button key={s} type="button" role="radio" aria-checked={shape === s} className={shape === s ? 'on' : ''} onClick={() => pickShape(s)}>{s === 'free' ? <Icon name="crop" size={16}/> : null}{shapeLabel(s)}</button>)}
        </div>
        {filters && <div className="pe-light">
          {slider('b', L('Brightness', 'Brillo'))}{slider('c', L('Contrast', 'Contraste'))}{slider('s', L('Color', 'Color'))}
        </div>}
        </div>
        <div className="fb-actions pe-foot">
          <button type="button" className="fb-primary" disabled={busy} onClick={() => void save()}>{busy ? '…' : L('Use photo', 'Usar foto')}</button>
          <button type="button" onClick={() => onDone(file)}>{L('Keep as it is', 'Dejar como está')}</button>
          <button type="button" disabled={!changed} onClick={reset}>{L('Start over', 'Empezar de nuevo')}</button>
        </div>
      </>}
    </div>
  </div>, document.body)
}
